import { ChevronDownIcon, InfoIcon } from '@chakra-ui/icons';
import {
  Button,
  Flex,
  FormControl,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorMessage, Form, Formik } from 'formik';
import * as Yup from 'yup';

import {
  getNoheadersStatus,
  getSelectedVersionDetails,
  getTableHeader,
  handleActionStepNext,
  resetSingleTimePeriodValue,
  setMultiTimePeriodValue,
  setTimePeriodRowIndex,
} from 'redux/DataImportSlice';
import { getModelTime } from 'redux/IndicatorPageSlice';
import { textStyles } from 'pages/ModelOverviewPage/Import/Helper';

import ActionStepFooter from '../../ActionStepFooter';
import { loadTimeRowOptions, TimeRowOption } from './timeRowOptions';

interface Properties {
  setHelpModalSeleceted?: any;
  getFormValue?: any;
}

const validationSchema = Yup.object().shape({
  selectedRowIndex: Yup.string().required('A time period row is required'),
});

const Columns = ({ setHelpModalSeleceted, getFormValue }: Properties) => {
  const dispatch: any = useDispatch();
  const headerNames = useSelector(getTableHeader);
  const selectedVersion = useSelector(getSelectedVersionDetails);
  const modelTime = useSelector(getModelTime);
  const noHeaders = useSelector(getNoheadersStatus);
  const [rowOptions, setRowOptions] = useState<TimeRowOption[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadError, setLoadError] = useState('');
  const VStackContainer: any = VStack;
  const MenuTrigger: any = MenuButton;
  const BodyText: any = Text;
  const FormControlContainer: any = FormControl;
  const FlexContainer: any = Flex;
  const MenuContainer: any = Menu;
  const MenuOptions: any = MenuList;
  const MenuGroup: any = MenuOptionGroup;
  const MenuOption: any = MenuItemOption;

  useEffect(() => {
    let isCancelled = false;

    const loadRowCandidates = async () => {
      if (!selectedVersion?.s3_file_url || !headerNames?.length || !modelTime?.time_range?.length) {
        setRowOptions([]);
        return;
      }

      setLoadingRows(true);
      setLoadError('');

      try {
        const options = await loadTimeRowOptions({
          s3FileUrl: selectedVersion.s3_file_url,
          headerNames,
          modelTimeRange: modelTime.time_range,
        });
        if (isCancelled) return;
        setRowOptions(options);
        setLoadingRows(false);
      } catch {
        if (isCancelled) return;
        setLoadError('Unable to read the selected file for time period detection.');
        setLoadingRows(false);
      }
    };

    loadRowCandidates();

    return () => {
      isCancelled = true;
    };
  }, [headerNames, modelTime?.time_range, noHeaders, selectedVersion?.s3_file_url]);

  const initialSelectedRowIndex = useMemo(() => {
    const saved = getFormValue?.timePeriodRowIndex;
    if (saved != null && rowOptions.some((row) => row.index === Number(saved))) {
      return String(saved);
    }

    if (rowOptions.length === 0) return '';

    const bestRow = rowOptions.reduce((best, current) =>
      current.matchedCount > best.matchedCount ? current : best,
    );
    return String(bestRow.index);
  }, [getFormValue?.timePeriodRowIndex, rowOptions]);

  const getRowLabel = (value: string) =>
    rowOptions?.find((row) => String(row.index) === String(value))?.label;

  const handleSubmit = async (
    form: { selectedRowIndex: string },
    setSubmitting: (data: boolean) => void,
  ) => {
    const selectedRow = rowOptions.find(
      (row) => String(row.index) === String(form.selectedRowIndex),
    );
    if (!selectedRow || selectedRow.mappings.length === 0) {
      setSubmitting(false);
      return;
    }

    await dispatch(setTimePeriodRowIndex(selectedRow.index));
    await dispatch(setMultiTimePeriodValue(selectedRow.mappings));
    await dispatch(resetSingleTimePeriodValue());
    await setSubmitting(false);
    await dispatch(handleActionStepNext());
  };

  return (
    <VStackContainer w='100%' pt={3}>
      <BodyText
        color='black'
        fontSize={textStyles.fontSize}
        fontFamily={textStyles.fontFamily}
        fontWeight={textStyles.fontWeight}
        textAlign='start'
        width='100%'
      >
        Select the row which has the time periods for each column. We&apos;ll auto-map any matching
        period labels to the model timeline.
      </BodyText>
      <Formik
        initialValues={{
          selectedRowIndex: initialSelectedRowIndex,
        }}
        validationSchema={validationSchema}
        onSubmit={(form, { setSubmitting }) => handleSubmit(form, setSubmitting)}
        enableReinitialize={true}
      >
        {({ values, errors, setFieldValue, isSubmitting }: any) => {
          const selectedRowLabel = getRowLabel(values?.selectedRowIndex);
          const rowPlaceholder = loadingRows ? 'Detecting rows…' : 'Select row';

          return (
            <Form
              style={{
                width: '100%',
              }}
            >
              <FormControlContainer pt={2}>
                <MenuContainer closeOnSelect={true} isLazy>
                  <MenuTrigger
                    as={Button}
                    backgroundColor='white'
                    maxHeight='40px'
                    borderRadius='5px'
                    minWidth='140px'
                    textAlign='left'
                    padding='0 .8rem'
                    boxShadow='rgb(139, 137, 177) 0px 0px 0px 0.6px'
                    rightIcon={<ChevronDownIcon fontSize='md' />}
                    fontWeight='300'
                    fontSize='xs'
                    color='black.900'
                    w='100%'
                    border={errors.selectedRowIndex ? '2px solid #fe6e6e' : '0.6px solid #8B89B1'}
                    isDisabled={loadingRows || rowOptions.length === 0}
                  >
                    <BodyText
                      me={3}
                      noOfLines={1}
                      height='fit-content'
                      opacity={selectedRowLabel ? 1 : 0.5}
                    >
                      {selectedRowLabel ?? rowPlaceholder}
                    </BodyText>
                  </MenuTrigger>
                  <MenuOptions
                    height='fit-content'
                    maxHeight='20vh'
                    overflowY='auto'
                    className='scroll'
                  >
                    <MenuGroup
                      defaultValue={values?.selectedRowIndex}
                      onChange={async (role: any) => {
                        await setFieldValue('selectedRowIndex', role);
                      }}
                    >
                      {rowOptions.map((row) => (
                        <MenuOption key={row.index} value={String(row.index)}>
                          {row.label}
                        </MenuOption>
                      ))}
                    </MenuGroup>
                  </MenuOptions>
                </MenuContainer>
                <ErrorMessage name='selectedRowIndex' component='div' className='error' />
              </FormControlContainer>

              {!loadingRows && rowOptions.length === 0 && (
                <BodyText color='red' fontSize='xs' pt={2}>
                  No valid time period rows were detected in the selected file.
                </BodyText>
              )}

              {loadError && (
                <BodyText color='red' fontSize='xs' pt={2}>
                  {loadError}
                </BodyText>
              )}

              <FlexContainer
                alignItems='center'
                justifyContent='space-between'
                pb={8}
                pt='5'
                w='100%'
              >
                <FlexContainer>
                  <BodyText
                    {...textStyles}
                    color='black'
                    textDecoration='underline'
                    onClick={() => {
                      setHelpModalSeleceted.on();
                    }}
                    display='none'
                  >
                    Help <InfoIcon color='#8B89B1' ms={3} />
                  </BodyText>
                </FlexContainer>
                <ActionStepFooter
                  nextButtonName='Continue'
                  prevButtonName='Previous'
                  isSubmitting={isSubmitting}
                  disableNext={loadingRows || rowOptions.length === 0}
                />
              </FlexContainer>
            </Form>
          );
        }}
      </Formik>
    </VStackContainer>
  );
};

export default Columns;
